export interface WorksCategory {
  id: number
  name: string
  slug: string
}

export interface WorksTag {
  id: number
  name: string
  slug: string
}

export interface WorksImage {
  ID?: number
  id?: number
  url?: string
  alt?: string
  title?: string
  caption?: string
  sizes?: {
    thumbnail?: string
    medium?: string
    large?: string
    full?: string
    [key: string]: string | undefined
  }
  width?: number
  height?: number
}

export interface WorksVideo {
  ID?: number
  id?: number
  title?: string
  filename?: string
  filesize?: number
  url?: string
  link?: string
  alt?: string
  author?: string
  description?: string
  caption?: string
  name?: string
  status?: string
  uploaded_to?: number
  date?: string
  modified?: string
  menu_order?: number
  mime_type?: string
  type?: string
  subtype?: string
  icon?: string
  width?: number
  height?: number
}

export interface WorksTextContent {
  acf_fc_layout: 'text'
  chars: string
}

export interface WorksImageContent {
  acf_fc_layout: 'image'
  source: WorksImage
  caption: string
}

export interface WorksVideoContent {
  acf_fc_layout: 'video'
  source: WorksVideo
  caption: string
}

export type WorksContent = WorksTextContent | WorksImageContent | WorksVideoContent

export interface StaffPost {
  ID: number
  post_title: string
  post_name?: string
  post_type?: string
  guid?: string
  acf?: {
    role?: string[] | string
    link?: string
    [key: string]: any
  }
  [key: string]: any
}

export interface WorksCreditRaw {
  staff_post?: StaffPost | number | null
  custom_role?: string[] | string
  custom_link?: string
  name?: string | any
  kindof?: string[] | string
  link?: string
}

export interface WorksCredit {
  role: string
  name: string
  link: string
  /** @deprecated 旧仕様互換（role と同値） */
  kindof: string
  staff_post?: StaffPost | number | null
  custom_role?: string[] | string
  custom_link?: string
}

export interface WorksItem {
  id: number
  title: string
  slug: string
  date: string
  modified: string
  link: string
  measures: WorksCategory[]
  featuredImage?: string
  description?: string
  contents?: WorksContent[]
  credits?: WorksCredit[]
  thumbnailUrl: string
  client: string
  business: WorksCategory[]
}

export function getWorksImageSrc(image?: WorksImage): string {
  if (!image) return ''
  if (image.sizes && image.sizes.large) return image.sizes.large
  return image.url || ''
}

export function getWorksVideoSrc(video?: WorksVideo): string {
  if (!video) return ''
  return video.url || video.link || ''
}

export function findFirstWorksImage(contents?: WorksContent[]): WorksImage | undefined {
  if (!contents) return undefined
  const imageContent = contents.find((content): content is WorksImageContent => {
    return content.acf_fc_layout === 'image' && !!(content as WorksImageContent).source
  })
  return imageContent ? imageContent.source : undefined
}

export function findFirstWorksVideo(contents?: WorksContent[]): WorksVideo | undefined {
  if (!contents) return undefined
  const videoContent = contents.find((content): content is WorksVideoContent => {
    return content.acf_fc_layout === 'video' && !!(content as WorksVideoContent).source
  })
  return videoContent ? videoContent.source : undefined
}

export function formatWorksItem(post: any): WorksItem {
  const embedded = post._embedded || {}
  const terms = embedded['wp:term'] || []
  const measures: any[] = []
  const client: any[] = []
  const business: any[] = []

  terms.forEach((group: any[]) => {
    if (!group || group.length === 0) return
    const taxonomy = group[0].taxonomy
    if (taxonomy === 'measures') {
      measures.push(...group)
    } else if (taxonomy === 'client') {
      client.push(...group)
    } else if (taxonomy === 'business') {
      business.push(...group)
    }
  })

  const featuredImage = embedded['wp:featuredmedia'] && embedded['wp:featuredmedia'][0]
    ? embedded['wp:featuredmedia'][0].source_url
    : ''

  const contents: WorksContent[] = (post.acf && post.acf.contents) || []
  const firstContentImage = findFirstWorksImage(contents)
  const thumbnailUrl = featuredImage || getWorksImageSrc(firstContentImage) || ''

  const rawCredits = (post.acf && post.acf.credits) || []
  const credits: WorksCredit[] = rawCredits.map((c: any) => {
    // 役割の取得（新仕様 custom_role 優先、未設定時はマスター role や旧 kindof をフォールバック）
    let role = ''
    const rawRole = (c.custom_role !== undefined && c.custom_role !== null && (Array.isArray(c.custom_role) ? c.custom_role.length > 0 : c.custom_role !== ''))
      ? c.custom_role
      : (c.role || c.kindof || (c.staff_post && c.staff_post.role) || (c.staff_post && c.staff_post.acf && c.staff_post.acf.role) || '')

    if (Array.isArray(rawRole)) {
      role = rawRole.filter(Boolean).join(' / ')
    } else if (typeof rawRole === 'string') {
      role = rawRole
    }

    // スタッフ名の取得（新仕様 staff_post 優先、未設定時は旧 name をフォールバック）
    let name = ''
    if (c.staff_post && typeof c.staff_post === 'object') {
      name = c.staff_post.post_title || (c.staff_post.title && c.staff_post.title.rendered) || c.staff_post.name || ''
    } else if (typeof c.staff_post === 'string') {
      name = c.staff_post
    } else if (Array.isArray(c.name)) {
      name = c.name
        .map((item: any) => (typeof item === 'object' && item !== null ? (item.post_title || item.name) : item))
        .filter(Boolean)
        .join('、')
    } else if (typeof c.name === 'object' && c.name !== null) {
      name = c.name.post_title || c.name.name || ''
    } else if (typeof c.name === 'string') {
      name = c.name
    }

    // リンクの取得（新仕様 custom_link 優先、未設定時は旧 link やスタッフマスターの link をフォールバック）
    let link = ''
    if (typeof c.custom_link === 'string' && c.custom_link.trim() !== '') {
      link = c.custom_link.trim()
    } else if (typeof c.link === 'string' && c.link.trim() !== '') {
      link = c.link.trim()
    } else if (c.staff_post && typeof c.staff_post === 'object') {
      if (typeof c.staff_post.link === 'string' && c.staff_post.link.trim() !== '') {
        link = c.staff_post.link.trim()
      } else if (c.staff_post.acf && typeof c.staff_post.acf.link === 'string') {
        link = c.staff_post.acf.link.trim()
      }
    }

    return {
      role,
      name,
      link,
      kindof: role,
      staff_post: c.staff_post,
      custom_role: c.custom_role,
      custom_link: c.custom_link
    }
  })

  return {
    id: post.id,
    title: post.title && post.title.rendered ? post.title.rendered : '',
    slug: post.slug,
    date: post.date,
    modified: post.modified,
    link: post.link,
    measures: measures.map((c: any) => ({ id: c.id, name: c.name, slug: c.slug })),
    featuredImage,
    description: post.acf && post.acf.description ? post.acf.description : '',
    contents,
    credits,
    thumbnailUrl,
    client: client.map((t: any) => t.name).join(' / '),
    business: business.map((c: any) => ({ id: c.id, name: c.name, slug: c.slug }))
  }
}
